import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { DataTable, Select, useSelect } from '@chilecompra/react-kit/components'
import routes from '../../config/settings/routes'
import { exportToCsv } from '../../modules/utils/exports'
import { formatAmount } from '../../modules/utils/formatters'
import generateYearsBetween from '../../modules/utils/years'
import { onGetSectorBuyersThunk } from './SectorBuyersSection.actions'
import DataLayout, { DataLayoutChild, DataLayoutViews, useDataLayout } from '../../components/Layouts/DataLayout'
import {
  Description,
  GridContainer,
  GridItem,
  Info,
  Link,
  Podium,
  Ranking,
  Root,
  Title
} from './SectorBuyersSection.styles'

const SortOrderDictionary = {
  amount: 'amount',
  orders: 'oc'
}

/**
 * The SectorBuyersSection's container.
 */
const SectorBuyersSection = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const {
    endYear,
    latestUpdate,
    loading,
    modalities: mechanismOptions,
    startYear
  } = useSelector(state => state.topNavbar)
  const { id, name } = useSelector(state => state.sectorSection)
  const { csv, data } = useSelector(state => state.sectorBuyersSection)
  const { value: yearValue, onChange: handleChangeYear } = useSelect({
    initialValue: endYear,
    overwriteCallback: () => endYear
  })
  const { value: mechanismValue, onChange: handleChangeMechanism } = useSelect({ initialValue: 7 })
  const { ref: providersDataLayoutRef, onDownloadFile: handleProvidersDownloadFile } = useDataLayout({
    dependencies: [csv, yearValue],
    downloadFileCallback: () => exportToCsv(csv, `Info_Sector_Compradores_${yearValue}`)
  })
  const yearsOptions = useMemo(() => generateYearsBetween(endYear, startYear), [endYear, startYear])
  const handleSortColumn = useCallback((_, updatedOrder) =>
    dispatch(
      onGetSectorBuyersThunk({
        id,
        mechanismId: mechanismValue,
        order: SortOrderDictionary[updatedOrder],
        year: yearValue
      })
    )
  )
  const handleClickRedirect = dni => () => navigate(`${routes.organization.replace(/:idEntity/, dni)}`)

  useEffect(() => {
    if (!id || !mechanismValue || !yearValue) {
      return
    }

    dispatch(
      onGetSectorBuyersThunk({ id, mechanismId: mechanismValue, order: SortOrderDictionary.amount, year: yearValue })
    )
  }, [id, mechanismValue, yearValue])

  return (
    <Root>
      <GridContainer padding="25px 0 23px">
        <GridItem xs={12}>
          <Title>Organismos compradores del sector</Title>
        </GridItem>
      </GridContainer>
      <GridContainer padding="0 0 28px">
        <GridItem xs={12}>
          <Description>
            Revisa el monto total transado y el número de órdenes de compra de cada organismo comprador perteneciente al
            sector {name} según el año seleccionado.
          </Description>
        </GridItem>
      </GridContainer>
      <GridContainer>
        <GridItem xs={12}>
          <DataLayout
            disableCaptureImage
            disableHeader
            initialDisplayedView={DataLayoutViews.TABLE}
            noDataText="El organismo comprador no cuenta con órdenes de compra que requieran una ficha de contrato en el año seleccionado."
            ref={providersDataLayoutRef}
            updatedDate={latestUpdate}
            onDownloadFile={handleProvidersDownloadFile}
          >
            <DataLayoutChild view={DataLayoutViews.TABLE}>
              <GridContainer padding="0 0 24px">
                <GridItem xs={12}>
                  <DataTable
                    enableResults
                    header={
                      <GridContainer alignItems="center" spacing={1}>
                        <GridItem md={2} xs={12}>
                          <Select
                            label="AÑO"
                            loading={loading}
                            options={yearsOptions}
                            value={yearValue}
                            onChange={handleChangeYear}
                          />
                        </GridItem>
                        <GridItem md={3} xs={12}>
                          <Select
                            label="MECANISMO DE COMPRA"
                            loading={loading}
                            options={mechanismOptions}
                            value={mechanismValue}
                            onChange={handleChangeMechanism}
                          />
                        </GridItem>
                        <GridItem md={7} xs={12} />
                      </GridContainer>
                    }
                    headers={[
                      { label: 'Ranking', value: 'ranking' },
                      { label: 'Nombre proveedor', value: 'name' },
                      { label: 'N° de órdenes de compra', value: 'orders' },
                      { label: 'Monto transado', value: 'amount' },
                      { label: 'Monto transado ($USD)', value: 'amountUSD' }
                    ]}
                    // FIXME: there is no way to filter using the related service.
                    headersCellsProps={[
                      { width: '15%' },
                      { width: '40%' },
                      { enableSort: true, width: '15%' },
                      { enableSort: true, initialSort: true, width: '15%' },
                      { width: '15%' }
                    ]}
                    loading={loading}
                    records={data}
                    recordsDecorators={[
                      ({ ranking }) =>
                        [1, 2, 3].indexOf(ranking) !== -1 ? (
                          <Podium>{ranking}</Podium>
                        ) : (
                          <Ranking>{ranking || ''}</Ranking>
                        ),
                      ({ id, name }) => <Link onClick={handleClickRedirect(id)}>{name}</Link>,
                      ({ orders }) => orders,
                      ({ amount }) => `$${formatAmount(amount)}`,
                      ({ amountUSD }) => `$${formatAmount(amountUSD)}`
                    ]}
                    onSortColumn={handleSortColumn}
                  />
                </GridItem>
              </GridContainer>
              <GridContainer margin="6px 0 0">
                <GridItem xs={12}>
                  <Info>
                    La información de montos transados dentro de esta sección corresponde al monto bruto (monto neto +
                    impuestos)
                  </Info>
                </GridItem>
              </GridContainer>
            </DataLayoutChild>
          </DataLayout>
        </GridItem>
      </GridContainer>
    </Root>
  )
}

export default SectorBuyersSection
